/**
 * Registering the offline shell's worker — §10 Phase 4.
 *
 * The whole platform seam is this file: the app never talks to a worker, never posts it a message
 * and never asks it anything. That is deliberate rather than minimal — the worker's only job is to
 * make a cold start work with no connection, and a page that had to *coordinate* with it would have
 * a second source of truth about what is cached.
 *
 * Nothing here has an Android equivalent and nothing needs one, which is why it is a platform file
 * with no core counterpart. What core owns is the routing table (`classifyServiceWorkerRequest`),
 * because that is where §10's constraint lives.
 */

/**
 * Register after `load`, not during boot.
 *
 * The install step fetches the whole precache — the shell is around a megabyte, most of it Shaka —
 * and starting that while the page is still fetching the same files for itself makes the first paint
 * slower to buy an offline capability that is worthless until the second visit. `load` has already
 * fired by the time anyone could go offline.
 */
export function registerServiceWorker(): void {
  /*
   * Production only. In dev the app is served by Vite with HMR over its own module graph, and a
   * worker holding a precache of a previous *preview* build would serve stale modules into it. The
   * two are separate origins today (`:5173` against `:4173`, which core's origin test treats as
   * different — `service-worker.test.ts` says so), so this is belt as well as braces; the braces
   * matter the day someone previews on the dev port.
   */
  if (!import.meta.env.PROD) return;
  if (!('serviceWorker' in globalThis.navigator)) return;

  const register = (): void => {
    void globalThis.navigator.serviceWorker
      .register('/sw.js', {
        /*
         * The worker script itself is never answered from the HTTP cache. A host that serves it with
         * a long `max-age` — the same policy every other file in `dist/` wants — would otherwise
         * hide a new build from the browser's update check for as long as that header says, and the
         * update check is the only thing that ever refreshes the precache.
         */
        updateViaCache: 'none',
      })
      .catch(() => {
        // A registration that fails costs the offline shell and nothing else: every request the app
        // makes is already written to work with no worker installed. There is nothing to tell the
        // reader and nothing to retry.
      });
  };

  if (globalThis.document.readyState === 'complete') register();
  else globalThis.addEventListener('load', register, { once: true });
}
